// src/utils/verificationUtils.js
import { collection, addDoc, getDocs, Timestamp } from 'firebase/firestore';
import { db } from
 '../firebase';

/**
 * Generate a SHA-256 hash string (browser-compatible)
 * @param {string} message - The message to hash
 * @returns {Promise<string>} - The hex hash string
 */
export const sha256 = async (message) => {
  // Encode the message as UTF-8
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  
  // Hash the message using the Web Crypto API
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  
  // Convert the hash to a hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex;
};

/**
 * Generate a unique verification ID for sanctions screenings
 * @param {string} searchTerm - The search term used
 * @param {number} timestamp - The timestamp when the search was performed
 * @returns {Promise<string>} - The generated verification ID
 */
export const generateVerificationId = async (searchTerm, timestamp) => {
  // Create date code in DDMMYY format (instead of YYMMDD)
  const date = new Date(timestamp);
  const year = date.getUTCFullYear().toString().substr(-2);
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = date.getUTCDate().toString().padStart(2, '0');
  const dateCode = `${day}${month}${year}`;
  
  // Create a server salt (this would normally be stored in an environment variable)
  const serverSalt = "SANCTIONS_SCREENER_SALT";
  
  // Concatenate the search term, timestamp, and salt
  const combined = `${searchTerm}|${timestamp}|${serverSalt}`;
  
  // Hash the combined string
  const hash = await sha256(combined);
  const entityHash = hash.substring(0, 6).toUpperCase();
  
  // Generate a random 4-digit number
  const random = Math.floor(1000 + Math.random() * 9000);
  
  // Assemble the verification ID
  return `SCR-${dateCode}-${entityHash}-${random}`;
};

/**
 * Get all distinct data sources from the sanctions collection
 * @returns {Promise<string[]>} - Array of distinct data sources
 */
export const getAllDataSources = async () => {
  try {
    // Get a reference to the 'sanctions' collection
    const sanctionsRef = collection(db, 'sanctions');
    
    // Get all documents (consider pagination for large collections)
    const querySnapshot = await getDocs(sanctionsRef);
    
    // Extract unique data sources
    const allSources = new Set();
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.dataSources && Array.isArray(data.dataSources)) {
        data.dataSources.forEach(source => {
          allSources.add(source);
        });
      }
    });
    
    // Convert Set to Array and return
    return Array.from(allSources);
  } catch (error) {
    console.error("Error fetching data sources:", error);
    return ["sanctions"]; // Fallback to default source
  }
};

/**
 * Get user's timezone information
 * @returns {Object} - Object containing timezone offset and name
 */
export const getUserTimezone = () => {
  const now = new Date();
  
  // Get timezone offset in minutes
  const offsetMinutes = now.getTimezoneOffset() * -1;
  const offsetHours = Math.abs(Math.floor(offsetMinutes / 60));
  const offsetMins = Math.abs(offsetMinutes % 60);
  
  // Format offset as +/-HH:MM
  const offsetSign = offsetMinutes >= 0 ? '+' : '-';
  const offsetFormatted = `${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMins.toString().padStart(2, '0')}`;
  
  // Get timezone name if available (not supported in all browsers)
  let timezoneName = '';
  try {
    timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (e) {
    console.warn('Timezone name not available', e);
  }
  
  return {
    offset: offsetFormatted,
    minutes: offsetMinutes,
    name: timezoneName
  };
};

/**
 * Format a date for local display
 * @param {Date} date - The date to format
 * @returns {string} - Formatted date string
 */
export const formatLocalDateTime = (date) => {
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

/**
 * Save search history to Firestore
 * @param {Object} params - The parameters for the search history record
 * @param {string} params.userId - The ID of the user who performed the search
 * @param {string} params.searchTerm - The search term used
 * @param {Object} params.stats - Statistics about the search results
 * @param {Array} params.results - The search results
 * @param {string} params.verificationId - The generated verification ID
 * @returns {Promise<string>} - The ID of the created record
 */
export const saveSearchHistory = async ({ userId, searchTerm, stats, results, verificationId }) => {
  try {
    // Get a reference to the 'sanction-searches' collection
    const searchHistoryRef = collection(db, 'sanction-searches');
    
    // Get all distinct data sources from the sanctions collection
    const allDataSources = await getAllDataSources();
    
    // If no sources were found, use a default value
    if (allDataSources.length === 0) {
      allDataSources.push('sanctions');
    }
    
    // Get current time in UTC
    const now = new Date();
    
    // Get user's timezone information
    const timezone = getUserTimezone();
    
    // For convenience, also create a formatted local time string
    const localTimeFormatted = formatLocalDateTime(now);
    
    // Extract matched record IDs from results
    const matchedRecords = results.map(result => result.id);
    
    // Prepare the data to save
    const searchData = {
      userId,
      // Store both as Firestore timestamps
      searchTimestampUTC: Timestamp.fromDate(now),
      searchTimestampLocal: Timestamp.fromDate(now), // Same moment in time
      
      // Store timezone information to interpret the local timestamp
      timezone: {
        offset: timezone.offset,
        offsetMinutes: timezone.minutes,
        name: timezone.name
      },
      
      // Also store a pre-formatted local time string for easy display
      searchTimeFormatted: localTimeFormatted,
      
      searchTerm,
      recordsScanned: stats.totalDocumentsQueried || 0,
      matchesFound: stats.totalMatches || 0,
      matchedRecords: matchedRecords,
      sourcesChecked: allDataSources,
      verificationId
    };
    
    // Add the document to Firestore
    const docRef = await addDoc(searchHistoryRef, searchData);
    console.log("Search history saved with ID:", docRef.id);
    
    return docRef.id;
  } catch (error) {
    console.error("Error saving search history:", error);
    throw error;
  }
};